import { categories as categoryData } from "@/mocks/categories";
import { news as newsData } from "@/mocks/news";
import { specials as specialData } from "@/mocks/specials";
import type { Category, NewsItem, Special, SortKey } from "@/types/news";

export const categories = categoryData as Category[];
export const newsList = newsData as NewsItem[];
export const specials = specialData as Special[];

export function getCategoryById(id: string): Category | undefined {
  return categories.find((c) => c.id === id);
}

export function getCategoryName(id: string): string {
  return getCategoryById(id)?.name ?? "资讯";
}

export function getNewsById(id: string): NewsItem | undefined {
  return newsList.find((n) => n.id === id);
}

export function getSpecialById(id: string): Special | undefined {
  return specials.find((s) => s.id === id);
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (v: number) => String(v).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function formatDateShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (v: number) => String(v).padStart(2, "0");
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return String(views);
}

function sortNews(list: NewsItem[], sort: SortKey): NewsItem[] {
  const copy = [...list];
  if (sort === "views") {
    copy.sort((a, b) => b.views - a.views);
  } else {
    copy.sort((a, b) => new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime());
  }
  return copy;
}

export function getLatestNews(limit?: number): NewsItem[] {
  const sorted = sortNews(newsList, "time");
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getHotNews(limit?: number): NewsItem[] {
  const sorted = sortNews(newsList, "views");
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getNewsByCategory(
  categoryId: string,
  subCategory?: string,
  sort: SortKey = "time"
): NewsItem[] {
  let list = newsList.filter((n) => n.categoryId === categoryId);
  if (subCategory) list = list.filter((n) => n.subCategory === subCategory);
  return sortNews(list, sort);
}

export function getRelatedNews(item: NewsItem, limit = 4): NewsItem[] {
  const sameCategory = newsList.filter((n) => n.categoryId === item.categoryId && n.id !== item.id);
  const sorted = sortNews(sameCategory, "views");
  if (sorted.length >= limit) return sorted.slice(0, limit);
  const extra = getHotNews().filter((n) => n.id !== item.id && !sorted.some((s) => s.id === n.id));
  return [...sorted, ...extra].slice(0, limit);
}

export function searchNews(keyword: string, categoryId?: string, sort: SortKey = "time"): NewsItem[] {
  const kw = keyword.trim().toLowerCase();
  let list = newsList;
  if (categoryId) list = list.filter((n) => n.categoryId === categoryId);
  if (kw) {
    list = list.filter((n) => {
      const haystack = `${n.title} ${n.summary} ${n.tags.join(" ")} ${n.subCategory}`.toLowerCase();
      return haystack.includes(kw);
    });
  }
  return sortNews(list, sort);
}

export interface Paginated<T> {
  items: T[];
  total: number;
  totalPages: number;
  page: number;
}

export function paginate<T>(list: T[], page: number, pageSize: number): Paginated<T> {
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    total,
    totalPages,
    page: safePage,
  };
}

export function getSpecialNews(special: Special): NewsItem[] {
  return special.newsIds
    .map((id) => getNewsById(id))
    .filter((n): n is NewsItem => Boolean(n));
}

export function getRelatedSpecials(currentId: string, limit = 3): Special[] {
  return specials.filter((s) => s.id !== currentId).slice(0, limit);
}